/**
 * UsageWarningBanner Component
 *
 * Displays a prominent warning banner at the top of the page when usage reaches 95%.
 * Features:
 * - Dismissible (but reappears on next login)
 * - Shows queries remaining
 * - Prominent "Upgrade" CTA
 * - Persists dismissal state in localStorage
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X, ArrowRight } from 'lucide-react';

interface UsageWarningBannerProps {
  usage: number;
  limit: number;
  percentage: number;
}

export default function UsageWarningBanner({ usage, limit, percentage }: UsageWarningBannerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if banner was previously dismissed (only for current session)
  useEffect(() => {
    const dismissed = sessionStorage.getItem('usage-warning-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  // Show banner only when usage is between 95% and 100%
  const shouldShow = percentage >= 95 && percentage < 100 && !isDismissed;

  if (!shouldShow) return null;

  const remaining = Math.max(0, limit - usage);

  const handleDismiss = () => {
    setIsDismissed(true);
    // Store dismissal in session storage (will reappear on next login)
    sessionStorage.setItem('usage-warning-dismissed', 'true');
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  return (
    <div className="bg-orange-50 border-b-2 border-orange-500 shadow-md">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-orange-600 animate-pulse" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-900">
                {t('usage.warning.queriesRemaining', { count: remaining })}
              </p>
              <p className="text-xs text-orange-700">
                {t('usage.warning.description', { usage, limit })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleUpgrade}
              className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-orange-700 transition-colors text-sm whitespace-nowrap"
            >
              {t('usage.warning.upgradeNow')}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-orange-100 rounded-lg transition-colors flex-shrink-0"
              aria-label={t('usage.warning.dismiss')}
            >
              <X className="w-5 h-5 text-orange-700" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
